/**
 * Convert a stored comma-separated ID list into unique positive integers.
 *
 * @param {string|number[]} value Stored attribute value or an array of IDs.
 * @return {number[]} Normalized IDs in their original order.
 */
export function parseContentIds(value) {
	let values = [];
	if (Array.isArray(value)) {
		values = value;
	} else if (typeof value === 'string') {
		values = value.split(',');
	}

	return values.reduce((ids, candidate) => {
		const normalized = String(candidate).trim();
		if (!/^\d+$/.test(normalized)) {
			return ids;
		}

		const id = Number.parseInt(normalized, 10);
		if (id > 0 && !ids.includes(id)) {
			ids.push(id);
		}

		return ids;
	}, []);
}

/**
 * Serialize content IDs using the block's established attribute format.
 *
 * @param {string|number[]} ids Content IDs.
 * @return {string} Comma-separated unique positive IDs.
 */
export function serializeContentIds(ids) {
	return parseContentIds(ids).join(',');
}

/**
 * Build a core REST search request for selectable sitemap content.
 *
 * @param {Object}   options            Request options.
 * @param {string}   [options.search]   Search phrase.
 * @param {number[]} [options.include]  IDs to resolve.
 * @param {number[]} [options.exclude]  IDs to omit.
 * @param {string[]} [options.postTypes] Allowed post-type slugs.
 * @param {number}   [options.perPage]  Requested result count.
 * @return {string} Relative WordPress REST path.
 */
export function buildContentSearchPath({
	search = '',
	include = [],
	exclude = [],
	postTypes = [],
	perPage = 20,
} = {}) {
	const resultCount = Number.parseInt(perPage, 10) || 20;
	const query = [
		'type=post',
		`per_page=${Math.min(100, Math.max(1, resultCount))}`,
		'_fields=id,title,subtype,url',
	];
	const subtypes = Array.from(
		new Set(
			postTypes.map((postType) => String(postType).trim()).filter(Boolean)
		)
	);
	const includedIds = parseContentIds(include);
	const excludedIds = parseContentIds(exclude);
	const searchTerm = String(search).trim();

	if (subtypes.length > 0) {
		query.push(`subtype=${encodeURIComponent(subtypes.join(','))}`);
	}
	if (includedIds.length > 0) {
		query.push(`include=${includedIds.join(',')}`);
	}
	if (excludedIds.length > 0) {
		query.push(`exclude=${excludedIds.join(',')}`);
	}
	if (searchTerm) {
		query.push(`search=${encodeURIComponent(searchTerm)}`);
	}

	return `/wp/v2/search?${query.join('&')}`;
}
